'use client';

import {useState, useEffect, useContext, useRef, useLayoutEffect} from 'react';
import DataContext from '@/contexts/DataContext';
import { fetchExprData, fetchTooltip, extractTrackedParams, extractChangedParams } from '@/lib/dataHandlers/DataFetchers';
import { useComponentLogger } from '@/lib/component-logger';
import { useTheme, useMediaQuery } from '@mui/material';

import { InfoTooltip } from './info-tooltip';
import Stack from '@mui/material/Stack';
import { textColors } from '@/styles/theme/colors';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { minHeight } from '@mui/system';

export function StatusText({config, notifyParentLineCount, titleMinHeight='1em', lineHeight=1.2}) {
    const logger = useComponentLogger("Status Text");
    const { currentProject, selectedViewParams, prelimParamCount, changedParams } = useContext(DataContext);
    const [tooltip, setTooltip] = useState(null);
    const [textContent, setTextContent] = useState(null);
    const [textTitle, setTextTitle] = useState(config.title || null);
    const [textColor, setTextColor] = useState('text.primary');
    const [contentLoading, setContentLoading] = useState(true);
    const [scale, setScale] = useState(1);
    const trackedParamValsRef = useRef({});
    const trackedParamNamesRef = useRef({}); // names of the params that this component depends on, extracted from its api expr

    const titleRef = useRef(null);
    const theme = useTheme();

    const isXs = useMediaQuery(theme.breakpoints.only('xs'));
    const isSm = useMediaQuery(theme.breakpoints.only('sm'));
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

    const curBreakpoint = isXs ? 'xs' : isSm ? 'sm' : 'md';

    const fontSize = isXs
    ? 16 // 1rem
    : isSm
    ? 20 // 1.25rem
    : 24; // 1.5rem


    useLayoutEffect(() => {
        const el = titleRef.current;
        if (!el || !textTitle) return;

        const styles = window.getComputedStyle(el);
        // const lineHeight = parseFloat(styles.lineHeight) || 1.2 * fontSize;
        const height = lineHeight * fontSize;

        const lines = Math.round(el.scrollHeight / height);

        // setLineCount((prev) => (prev === lines ? prev : lines));
        // notifyParentLineCount?.(curBreakpoint, lines);
        notifyParentLineCount?.(lines);

        logger.debug('line count: ', lines, textTitle)
    }, [textTitle, isXs, isSm, isMdUp]); // recalc if breakpoint changes

    useEffect(() => {
        logger.debug('min height', titleMinHeight, textTitle);
    }, [titleMinHeight])

    // fetch and set content
    const fetchContent = async () => {
        if (config.dataSource === null || config.dataSource === undefined) return;

        const dataSource = config.dataSource;

        if (dataSource.type === "text") {
            setTextContent(config.dataSource.text);
        } else if (dataSource.type === "apiExpr") {
            const data = await fetchExprData(currentProject, selectedViewParams, dataSource.expr);

            if (data?.rows && data.rows.length > 0) {
                let apiConfig = data.rows[0];
                if (apiConfig.title) {
                    setTextTitle(apiConfig.title);
                }
                if (apiConfig.primary) {
                    setTextContent(apiConfig.primary);
                }
                if (apiConfig.color) {
                    if (textColors[apiConfig.color]) {
                        setTextColor(textColors[apiConfig.color]);
                    } else {
                        setTextColor('text.primary');
                    }
                }
            }
        }

        setContentLoading(false);
    }

    const processTooltip = async() => {
        let tooltipContent = await fetchTooltip(currentProject, selectedViewParams, config);
        if (tooltipContent) setTooltip(tooltipContent);
    }

    // Add function to calculate scale
    const calculateScale = (element) => {
        if (!element) return;
        const parent = element.parentElement;
        if (!parent) return;
        
        const parentWidth = parent.clientWidth;
        const textWidth = element.scrollWidth;
        
        if (textWidth > parentWidth) {
            setScale((parentWidth / textWidth) * 0.95); // 0.95 adds a small safety margin
        } else {
            setScale(1);
        }
    };

    useEffect(() => {
        setContentLoading(true);
        let trackedParams = extractTrackedParams(config.dataSource);
        let params = {};
        for (const param of trackedParams) {
            if (selectedViewParams[param]) params[param] = selectedViewParams[param].value;
        }

        logger.debug('tracked params', params);
        trackedParamValsRef.current = params;
        trackedParamNamesRef.current = trackedParams;
        fetchContent().catch(() => {});
        processTooltip().catch(() => {});
    }, []);

    useEffect(() => {
         let shouldReFetch = extractChangedParams(trackedParamNamesRef.current, changedParams, selectedViewParams, trackedParamValsRef.current);
        logger.debug('changed params', changedParams, trackedParamNamesRef.current, shouldReFetch);
        if (shouldReFetch.changed) {
            trackedParamValsRef.current = shouldReFetch.paramRef;
            fetchContent();
            processTooltip();
        }

        // make sure to clear the content if any of the tracked params were removed
        // common use case: when a parent param changes, it clears the child param
        for (const param of trackedParamNamesRef.current) { 
            if (!selectedViewParams[param]) {
                setTextContent(null);
                return;
            }
        }
        logger.debug("changed params", changedParams, trackedParamNamesRef.current, shouldReFetch);
    }, [changedParams]);

    if (textContent === null && !config.title) return null;

    return textContent ? (
        <Box 
            sx={{
                width: '100%',
                height: '100%',
                padding: {
                    xs: 2,    // 16px padding on mobile
                    sm: 3,    // 24px padding on tablet
                    md: 4     // 32px padding on desktop
                },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
            }}
        >
            <Stack 
                spacing={2} 
                sx={{
                    width: '100%',
                    textAlign: 'center'
                }}
            >
                <Box
                    sx={{
                        minHeight: titleMinHeight,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}  
                >
                {textTitle && (
                    <Typography 
                        ref={titleRef}
                        color="text.secondary" 
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: {
                                xs: '1rem',      // 16px on mobile
                                sm: '1.25rem',   // 20px on tablet
                                md: '1.5rem'     // 24px on desktop
                            },
                            lineHeight: lineHeight ?? 1.2,
                            // marginBottom: 1,    
                        }}
                    >
                        {textTitle}
                        {tooltip && <InfoTooltip content={tooltip} />}
                    </Typography>
                )}
                </Box>
                
                <Typography 
                    color={textColor}
                    ref={(el) => el && calculateScale(el)}
                    sx={{
                        fontSize: {
                            xs: '2rem',    // Base size for mobile
                            sm: '2.5rem',  // Base size for tablet
                            md: '3rem'     // Base size for desktop
                        },
                        lineHeight: lineHeight ?? 1.2,
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        transform: `scale(${scale})`,
                        transformOrigin: 'center center',
                        transition: 'transform 0.2s ease',
                        margin: '0 auto'
                    }}
                >
                    {textContent}
                </Typography>
            </Stack>
        </Box>
    ) : null;
}